import React, { useState } from "react";
import { Button, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import "./Cards.css";
import moment from "moment";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import { alpha } from "@mui/material/styles";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import CloseIcon from "@mui/icons-material/Close";

// File-type badge colors for artifact cards; anything unlisted falls back to grey.
const FILE_TYPE_COLORS = {
  pdf: "#d32f2f",
  doc: "#1565c0",
  docx: "#1565c0",
  txt: "#1565c0",
  xls: "#2e7d32",
  xlsx: "#2e7d32",
  csv: "#2e7d32",
  jpg: "#ef6c00",
  jpeg: "#ef6c00",
  png: "#ef6c00",
  gif: "#ef6c00",
  webp: "#ef6c00",
  heic: "#ef6c00",
};
const IMAGE_FILE_TYPES = ["jpg", "jpeg", "png", "gif", "webp"];

const artifactCardSx = {
  border: "1px solid",
  borderColor: "divider",
  borderRadius: 2,
  bgcolor: "background.paper",
  transition: "border-color 0.15s, box-shadow 0.15s",
  "&:hover": { borderColor: "#3fa7d6", boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)" },
};

const artifactNameSx = {
  display: "block",
  maxWidth: "100%",
  fontWeight: 600,
  fontSize: "0.95rem",
  textAlign: "left",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const FileTypeBadge = ({ fileType, size = 44 }) => {
  const ext = (fileType || "").toLowerCase();
  const color = FILE_TYPE_COLORS[ext] || "#607d8b";
  return (
    <Box
      sx={{
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: 1.5,
        bgcolor: alpha(color, 0.12),
        color,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: `${Math.round(size * 0.26)}px`,
        fontWeight: 700,
        letterSpacing: 0.5,
      }}
    >
      {ext ? ext.toUpperCase().slice(0, 4) : "FILE"}
    </Box>
  );
};

const ArtifactMeta = ({ taskName, empName, date }) => {
  const items = [
    taskName && { key: "task", icon: <AssignmentOutlinedIcon />, text: taskName },
    empName && empName.trim() && { key: "emp", icon: <PersonOutlineIcon />, text: empName },
    date && { key: "date", icon: <CalendarTodayOutlinedIcon />, text: moment(date).format("MMM DD, YYYY hh:mm A") },
  ].filter(Boolean);
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", columnGap: 1.5, rowGap: 0.25, mt: 0.25, color: "text.secondary", fontSize: "0.8rem" }}>
      {items.map((item) => (
        <Box key={item.key} sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0, "& svg": { fontSize: "1rem" } }}>
          {item.icon}
          <Box sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.text}</Box>
        </Box>
      ))}
    </Box>
  );
};

const ArtifactActions = ({ url, isAbilityHub, onInfo, onDelete }) => (
  <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
    {isAbilityHub && (
      <Tooltip title="AbilityHUB">
        <Box sx={{ mr: 0.5, px: 0.75, height: 20, borderRadius: 10, bgcolor: "primary.main", color: "primary.contrastText", fontSize: "0.65rem", fontWeight: 700, display: "flex", alignItems: "center" }}>
          AH
        </Box>
      </Tooltip>
    )}
    <Tooltip title="Details">
      <IconButton size="small" onClick={onInfo}>
        <InfoOutlinedIcon fontSize="small" />
      </IconButton>
    </Tooltip>
    <Tooltip title="Download">
      <IconButton size="small" component="a" href={url} target="_blank" rel="noopener noreferrer">
        <FileDownloadOutlinedIcon fontSize="small" />
      </IconButton>
    </Tooltip>
    {onDelete && !isAbilityHub && (
      <Tooltip title="Delete">
        <IconButton size="small" onClick={onDelete} sx={{ "&:hover": { color: "error.main" } }}>
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    )}
  </Box>
);

// A file (artifact) card, as a list row or a grid tile (`grid`), with a details dialog.
const ArtifactCard = (props) => {
  const [show, setShow] = useState(false);

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const artifactExt = (props.fileType || "").toLowerCase();
  const openArtifact = () => {
    if (props.openDocViewer) props.openDocViewer(props.url, props.fileType);
    else window.open(props.url, "_blank", "noopener,noreferrer");
  };

  return (
    <div>
      <div style={props.grid ? { height: "100%" } : undefined}>
        {!props.grid ? (
          <Box sx={{ ...artifactCardSx, display: "flex", alignItems: "center", gap: 1.5, px: 1.5, py: 1.25, mb: 1 }}>
            <FileTypeBadge fileType={props.fileType} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Tooltip title={props.fileName || ""} placement="top-start" enterDelay={500}>
                <Link component="button" underline="hover" onClick={openArtifact} sx={artifactNameSx}>
                  {props.fileName}
                </Link>
              </Tooltip>
              <ArtifactMeta taskName={props.taskName} empName={props.empName} date={props.date} />
            </Box>
            <ArtifactActions
              url={props.url}
              isAbilityHub={props.isAbilityHub}
              onInfo={handleShow}
              onDelete={props.del ? () => props.del(props.id) : null}
            />
          </Box>
        ) : (
          <Box sx={{ ...artifactCardSx, overflow: "hidden", display: "flex", flexDirection: "column", height: "100%" }}>
            <Box
              onClick={openArtifact}
              sx={{ height: 140, bgcolor: "#f5f7fa", borderBottom: "1px solid", borderColor: "divider", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
            >
              {IMAGE_FILE_TYPES.includes(artifactExt) ? (
                <Box component="img" src={props.url} alt={props.fileName} sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
              ) : artifactExt === "pdf" ? (
                <embed src={props.url} type="application/pdf" style={{ width: "100%", height: "100%", pointerEvents: "none" }} />
              ) : (
                <FileTypeBadge fileType={props.fileType} size={64} />
              )}
            </Box>
            <Box sx={{ p: 1.5, display: "flex", flexDirection: "column", gap: 0.5, flex: 1 }}>
              <Tooltip title={props.fileName || ""} placement="top-start" enterDelay={500}>
                <Link component="button" underline="hover" onClick={openArtifact} sx={artifactNameSx}>
                  {props.fileName}
                </Link>
              </Tooltip>
              <ArtifactMeta taskName={props.taskName} empName={props.empName} date={props.date} />
              <Box sx={{ mt: "auto", pt: 0.5, display: "flex", justifyContent: "flex-end" }}>
                <ArtifactActions
                  url={props.url}
                  isAbilityHub={props.isAbilityHub}
                  onInfo={handleShow}
                  onDelete={props.del ? () => props.del(props.id) : null}
                />
              </Box>
            </Box>
          </Box>
        )}
        <Dialog open={show} onClose={handleClose} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5, pr: 6 }}>
            <FileTypeBadge fileType={props.fileType} size={36} />
            <Box sx={{ minWidth: 0, wordBreak: "break-word", fontSize: "1.05rem" }}>{props.fileName}</Box>
            <IconButton aria-label="close" onClick={handleClose} sx={{ position: "absolute", right: 8, top: 8 }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            {[
              ["Task", props.taskName],
              ["Project", props.pname],
              ["Document Type", artifactExt.toUpperCase()],
              ["Uploaded on", props.date ? moment(props.date).format("MMM DD, YYYY hh:mm A") : ""],
              ["Uploaded by", props.empName],
            ].map(([label, value]) => (
              <Box key={label} sx={{ mb: 1.5, "&:last-of-type": { mb: 0 } }}>
                <Typography variant="caption" color="text.secondary">{label}</Typography>
                <Typography variant="body2">{value && String(value).trim() ? value : "—"}</Typography>
              </Box>
            ))}
          </DialogContent>
          <DialogActions>
            <Button component="a" href={props.url} target="_blank" rel="noopener noreferrer" startIcon={<FileDownloadOutlinedIcon />}>
              Download
            </Button>
            <Button variant="contained" onClick={() => { handleClose(); openArtifact(); }}>
              Open
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </div>
  );
};

export default ArtifactCard;
